import { ArrowUpRight, Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import CreatorSocialCard from "../Creators/CreatorSocialCard";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { api } from "@cvx/_generated/api";
import { usePaginatedQuery } from "convex/react";

interface Creator {
  _id: Id<"creators">;
  name?: string;
  bio?: string;
  profileImage: string | null;
  hasActivePartnership: boolean;
  hasPendingApplication: boolean;
  socials: {
    tiktok?: Array<{ handle: string; followers: number; posts: number }>;
    instagram?: Array<{ handle: string; followers: number; posts: number }>;
    youtube?: Array<{ handle: string; followers: number; posts: number }>;
  };
}

const SearchInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    <Input
      placeholder="Search creators..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pl-9"
    />
  </div>
);

const CreatorCard = ({
  creator,
  onClick,
}: {
  creator: Creator;
  onClick: () => void;
}) => (
  <div
    className="rounded-lg border border-border bg-card p-6 cursor-pointer hover:bg-secondary/50 transition-colors"
    onClick={onClick}
  >
    <div className="flex items-start gap-6 transition-colors">
      <img
        src={creator.profileImage ?? ""}
        alt={creator.name}
        className="h-16 w-16 rounded-full shrink-0 border-2 border-border"
      />
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-primary truncate mb-2">
          {creator.name}
        </h3>
        {creator.bio && (
          <p className="text-sm text-primary/60 line-clamp-2 mb-4">
            {creator.bio}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          {creator.socials.youtube?.map((youtube) => (
            <CreatorSocialCard
              key={youtube.handle}
              url={`https://www.youtube.com/results?search_query=${youtube.handle}`}
              platform="youtube"
              handle={youtube.handle}
              followers={youtube.followers}
              posts={youtube.posts}
            />
          ))}
          {creator.socials.instagram?.map((instagram) => (
            <CreatorSocialCard
              key={instagram.handle}
              url={`https://www.instagram.com/${instagram.handle}`}
              platform="instagram"
              handle={instagram.handle}
              followers={instagram.followers}
              posts={instagram.posts}
            />
          ))}
          {creator.socials.tiktok?.map((tiktok) => (
            <CreatorSocialCard
              key={tiktok.handle}
              url={`https://www.tiktok.com/@${tiktok.handle}`}
              platform="tiktok"
              handle={tiktok.handle}
              followers={tiktok.followers}
              posts={tiktok.posts}
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        {creator.hasActivePartnership && (
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-500">
            Active Partnership
          </span>
        )}
        {creator.hasPendingApplication && (
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-500">
            Pending Application
          </span>
        )}
        {!creator.hasActivePartnership && !creator.hasPendingApplication && (
          <ArrowUpRight className="h-5 w-5 text-primary/60" />
        )}
      </div>
    </div>
  </div>
);

interface CreatorsListProps {
  campaignId: Id<"campaigns"> | undefined;
  onCreatorClick: (creatorId: Id<"creators">) => void;
}

export const CreatorsList = ({
  campaignId,
  onCreatorClick,
}: CreatorsListProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    results: creators,
    status: creatorsStatus,
    loadMore: loadMoreCreators,
  } = usePaginatedQuery(
    api.creators.getPaginatedCreators,
    {
      campaignId,
      searchQuery: debouncedSearchQuery,
    },
    { initialNumItems: debouncedSearchQuery ? 100 : 30 },
  );

  return (
    <div className="flex flex-col gap-6">
      <SearchInput value={searchQuery} onChange={setSearchQuery} />
      <div className="grid grid-cols-1 gap-4">
        {creatorsStatus === "LoadingMore" ? (
          <div className="flex justify-center p-8">
            <Loader2 className="animate-spin" />
          </div>
        ) : creators && creators.length > 0 ? (
          creators.map((creator: Creator) => (
            <CreatorCard
              key={creator._id}
              creator={creator}
              onClick={() => onCreatorClick(creator._id)}
            />
          ))
        ) : (
          <div className="text-center text-primary/60 p-8">
            No creators found
          </div>
        )}
      </div>
      {creators && creators.length > 0 && creatorsStatus === "CanLoadMore" && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => loadMoreCreators(10)}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};
